(function (global) {
  'use strict';

  global.NewHomeSource = global.NewHomeSource || {};
  global.NewHomeSource.Carousel = global.NewHomeSource.Carousel || {};

  class SlidesInView {
    /**
     * Initializes a new instance of the class.
     * Sets up the intersection entry map, observer, and destroyed flag.
     *
     * @constructor
     */
    constructor() {
      this.intersectionEntryMap = {};
      this.intersectionObserver = null;
      this.destroyed = false;
    }

    /**
     * Initializes the IntersectionObserver to track which slides are in view.
     * Sets up the observer with the provided state, root node, and slides.
     *
     * @param {Object} state - The state object containing carousel data.
     */
    initialize(state) {
      this.state = state;
      const rootNode = this.state.get('rootNode');
      const slides = this.state.get('slides');
      this.intersectionObserver = new IntersectionObserver(this.intersectionChange.bind(this), {
        root: rootNode,
        threshold: 0
      });
      slides.forEach((slide) => this.intersectionObserver.observe(slide));
    }

    /**
     * Handles changes in intersection for carousel slides.
     * Updates the intersection entry map with the latest IntersectionObserverEntry for each slide,
     * then notifies observers of a carousel change.
     *
     * @param {IntersectionObserverEntry[]} entries - Array of intersection observer entries for the slides.
     */
    intersectionChange(entries) {
      const slides = this.state.get('slides');
      if (this.destroyed) {
        return;
      }

      entries.forEach((entry) => {
        const index = slides.indexOf(entry.target);
        this.intersectionEntryMap[index] = entry;
      });

      this.state.notifyObservers('carouselChange');
    }

    /**
     * Cleans up resources used by the instance.
     * Disconnects the IntersectionObserver if it exists and marks the instance as destroyed.
     */
    destroy() {
      if (this.intersectionObserver) {
        this.intersectionObserver.disconnect();
      }
      this.destroyed = true;
    }

    /**
     * Generates a list of slide indices based on their intersection status.
     *
     * @param {boolean} inView - If true, returns indices of slides currently in view; if false,
     * returns indices of slides not in view.
     * @returns {number[]} Array of slide indices matching the specified intersection status.
     */
    createInViewList(inView) {
      return Object.keys(this.intersectionEntryMap).reduce((list, slideIndex) => {
        const index = parseInt(slideIndex, 10);
        const isIntersecting = this.intersectionEntryMap[index].isIntersecting;
        const inViewMatch = inView && isIntersecting;
        const notInViewMatch = !inView && !isIntersecting;
        if (inViewMatch || notInViewMatch) {
          list.push(index);
        }

        return list;
      }, []);
    }

    /**
     * Returns an array of slide indexes that are currently in view.
     *
     * @returns {number[]} An array of indexes representing the slides in view.
     */
    getInView() {
      const slideIndexes = this.createInViewList(true);
      return slideIndexes;
    }

    /**
     * Returns an array of slide indexes that are not currently in view.
     *
     * @returns {number[]} An array of slide indexes not in view.
     */
    getNotInView() {
      const slideIndexes = this.createInViewList(false);
      return slideIndexes;
    }
  }

  global.NewHomeSource.Carousel.SlidesInView = SlidesInView;
}(window));
